import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  deleteField,
  doc,
  getDoc,
  onSnapshot,
  setDoc,
} from 'firebase/firestore';
import { auth, db, API_BASE_URL } from '../../firebase/firebaseConfig';
import {
  DetailShell,
  HCard,
  SectionLabel,
  Lede,
} from '../h';
import { SettingRow } from './settingControls';
import { useFamiliesData } from './useFamiliesData';
import { cn } from '../../lib/utils';

/* v9 mockup 4.5 — Device settings.

   This screen is a skin on top of the pre-FEAT-133 DeviceSettings
   (components/HomeDashboard/DeviceSettings.js). The settings catalog is
   assembled at runtime from Firestore, not hardcoded:

     1. Read deviceManagement/{familyID}/status/accountVisible to get
        currentAppVersion / targetAppVersion / instantUpdate. The effective
        APK version is targetVersion if instantUpdate is set and differs
        from current, else currentVersion.

     2. Read appUpdates/{effectiveVersion}/userVisible/userSettings. This
        doc maps { category: [settingName, …] } and acts as the allowlist
        of settings exposed for that APK version.

     2b. Resolve this Hearth's hardware version (its manufacturing batch) from
        the serial in the status doc, via /user/hearthVersion. Settings that
        declare a `compatibility` list are only shown when that list contains
        this Hearth's hardware version — see isCompatibleHardware below.

     3. For each allowed category, read:
          deviceSettings/everyAccountAndEveryFamilyVisible/settings/{category}
        for the per-setting schema (fields on the doc keyed by settingName,
        each with { type, value, label, description, tooltip, choices?,
        range?, step?, compatibility?, order }), and
          deviceSettings/.../settings/{category}/metadata/metadata
        for the category's { label, description, disabledInAutoMode }.

     4. Live-subscribe to deviceManagement/{familyID}/userSettings/{category}
        for user overrides. Each override doc is a flat map
        { [settingName]: overrideValue }.

     5. Live-subscribe to deviceManagement/{familyID}/overrideSettings/{category}
        for ADMIN overrides. The device resolves user ?? adminOverride ?? global,
        so what "Default" actually means for this Hearth is
        adminOverride ?? global — that's the value shown (and seeded) when the
        Default toggle is on. If this read fails (e.g. the rules don't allow it
        yet), we degrade to showing the bare global — same as before.

   Writes:
     - Set override:   merge  { [name]: value }   into userSettings/{cat}
     - Clear override: merge  { [name]: deleteField() } into userSettings/{cat}

   Co-Manager invite and Repeat Training were in the OLD DeviceSettings but
   have been moved out: Co-Manager lives (or will live) on HearthDetail's
   Co-Managers section, and Repeat Training is in HearthDetail's Actions.
   This screen is settings-only.
*/
/* A setting with no compatibility list runs on every Hearth. One that declares a
   list needs this Hearth's hardware version to appear in it. A version we could not
   resolve never matches, so a control that might do nothing on this hardware is
   hidden rather than shown. */
function isCompatibleHardware(compatibility, hardwareVersion) {
  if (!Array.isArray(compatibility) || compatibility.length === 0) return true;
  return hardwareVersion != null && compatibility.includes(hardwareVersion);
}

export function DeviceSettingsView() {
  const { familyID } = useParams();
  const navigate = useNavigate();

  const families = useFamiliesData([familyID]);
  const family = families[familyID] || {};
  const autoMode = family.autoMode === true;

  const [effectiveVersion, setEffectiveVersion] = useState(null);
  // undefined = not resolved yet, null = no serial / unknown to Hearth Records, string = batch.
  const [hearthSerial, setHearthSerial] = useState(undefined);
  const [hardwareVersion, setHardwareVersion] = useState(undefined);
  const [catalog, setCatalog] = useState(null); // { settings: [...], categoryMeta: {...} } | null while loading
  const [userValuesByCategory, setUserValuesByCategory] = useState({});
  const [adminOverridesByCategory, setAdminOverridesByCategory] = useState({});
  const [schemaError, setSchemaError] = useState(null);

  // 1. Live listen to device status to resolve the effective APK version.
  useEffect(() => {
    if (!familyID) return undefined;
    const unsub = onSnapshot(
      doc(db, 'deviceManagement', familyID, 'status', 'accountVisible'),
      (snap) => {
        if (!snap.exists()) { setEffectiveVersion(null); setHearthSerial(null); return; }
        const d = snap.data();
        const cur = d.currentAppVersion;
        const tgt = d.targetAppVersion;
        const instant = d.instantUpdate ?? false;
        const eff = (instant && tgt && tgt !== cur) ? tgt : cur;
        setEffectiveVersion(eff || null);
        setHearthSerial(d.hearthSerial || null);
      },
      (err) => {
        console.warn('device status listener error', err?.code || err);
        setEffectiveVersion(null);
        setHearthSerial(null);
      },
    );
    return () => unsub();
  }, [familyID]);

  // 1b. Resolve the Hearth's hardware version from its serial. Any failure — no serial,
  //     a serial with no manufacturing record, or the lookup being unreachable — settles
  //     as null, which hides every setting that declares a compatibility list.
  useEffect(() => {
    if (hearthSerial === undefined) return undefined;
    if (!hearthSerial) { setHardwareVersion(null); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const idToken = await auth.currentUser.getIdToken();
        const resp = await fetch(
          `${API_BASE_URL}/user/hearthVersion?serial=${encodeURIComponent(hearthSerial)}`,
          { headers: { Authorization: `Bearer ${idToken}` } },
        );
        const version = resp.ok ? ((await resp.json()).version ?? null) : null;
        if (!cancelled) setHardwareVersion(version);
      } catch (err) {
        console.warn('hearth version lookup failed', err);
        if (!cancelled) setHardwareVersion(null);
      }
    })();
    return () => { cancelled = true; };
  }, [hearthSerial]);

  // 2. When the effective version changes, load the allowlist, then fetch the
  //    per-setting schema and per-category metadata. One-shot reads — the
  //    schema rarely changes mid-session and the OLD code also did one-shots.
  useEffect(() => {
    let cancelled = false;
    if (effectiveVersion === null || hardwareVersion === undefined) {
      // Still resolving status or the hardware version; show loading until we
      // know one way or the other. Filtering before the hardware version has
      // settled would hide compatible settings on the first render.
      return undefined;
    }
    if (!effectiveVersion) {
      setCatalog({ settings: [], categoryMeta: {} });
      return undefined;
    }
    setSchemaError(null);
    (async () => {
      try {
        const versionDoc = await getDoc(
          doc(db, 'appUpdates', String(effectiveVersion), 'userVisible', 'userSettings'),
        );
        if (!versionDoc.exists()) {
          if (!cancelled) setCatalog({ settings: [], categoryMeta: {} });
          return;
        }
        const allowlist = versionDoc.data();
        const allSettings = [];
        const categoryMeta = {};
        for (const [category, names] of Object.entries(allowlist)) {
          if (!Array.isArray(names) || names.length === 0) continue;
          const [catDoc, metaDoc] = await Promise.all([
            getDoc(doc(
              db, 'deviceSettings', 'everyAccountAndEveryFamilyVisible',
              'settings', category,
            )),
            getDoc(doc(
              db, 'deviceSettings', 'everyAccountAndEveryFamilyVisible',
              'settings', category, 'metadata', 'metadata',
            )),
          ]);
          if (!catDoc.exists()) continue;
          const catData = catDoc.data();
          if (metaDoc.exists()) categoryMeta[category] = metaDoc.data();
          for (const name of names) {
            const meta = catData[name];
            if (!meta) continue;
            // Filtering here rather than at render time means a category whose settings
            // are all filtered out never produces an entry in `grouped`, so no empty
            // category card is drawn.
            if (!isCompatibleHardware(meta.compatibility, hardwareVersion)) continue;
            allSettings.push({ ...meta, name, category });
          }
        }
        allSettings.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        if (!cancelled) setCatalog({ settings: allSettings, categoryMeta });
      } catch (err) {
        console.error('DeviceSettingsView schema load error', err);
        if (!cancelled) {
          setSchemaError('Couldn\u2019t load this Hearth\u2019s settings.');
          setCatalog({ settings: [], categoryMeta: {} });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [effectiveVersion, hardwareVersion]);

  // 3. Live subscribe to userSettings per category. Setting up listeners
  //    once we know the categories keeps the UI reactive if the device
  //    pushes an update (or another tab changes a value).
  useEffect(() => {
    if (!catalog || !familyID) return undefined;
    const categories = [...new Set(catalog.settings.map((s) => s.category))];
    const unsubs = [];
    for (const category of categories) {
      unsubs.push(
        onSnapshot(
          doc(db, 'deviceManagement', familyID, 'userSettings', category),
          (snap) => {
            setUserValuesByCategory((prev) => ({
              ...prev,
              [category]: snap.exists() ? snap.data() || {} : {},
            }));
          },
          (err) => console.warn('userSettings listener', category, err?.code || err),
        ),
      );
    }
    return () => unsubs.forEach((u) => { try { u(); } catch (_) {} });
  }, [catalog, familyID]);

  // 3b. Live subscribe to ADMIN overrideSettings per category. These sit
  //     between the global value and the user's own choice in the device's
  //     resolution order, so they are what "Default" really resolves to.
  //     Fail-soft on purpose: if the read errors (most likely
  //     permission-denied because the updated Firestore rules aren't
  //     deployed yet), we fall back to the bare global value rather than
  //     breaking the screen.
  useEffect(() => {
    if (!catalog || !familyID) return undefined;
    const categories = [...new Set(catalog.settings.map((s) => s.category))];
    const unsubs = [];
    for (const category of categories) {
      unsubs.push(
        onSnapshot(
          doc(db, 'deviceManagement', familyID, 'overrideSettings', category),
          (snap) => {
            setAdminOverridesByCategory((prev) => ({
              ...prev,
              [category]: snap.exists() ? snap.data() || {} : {},
            }));
          },
          (err) => {
            console.warn('overrideSettings listener (fail-soft)', category, err?.code || err);
            setAdminOverridesByCategory((prev) => ({ ...prev, [category]: {} }));
          },
        ),
      );
    }
    return () => unsubs.forEach((u) => { try { u(); } catch (_) {} });
  }, [catalog, familyID]);

  /* What "Default" means for this Hearth: the admin override if one exists,
     else the global schema value. */
  const effectiveDefaultFor = (setting) => {
    const adminValue = adminOverridesByCategory[setting.category]?.[setting.name];
    return adminValue !== undefined ? adminValue : setting.value;
  };

  const saveValue = async (category, name, value) => {
    try {
      await setDoc(
        doc(db, 'deviceManagement', familyID, 'userSettings', category),
        { [name]: value },
        { merge: true },
      );
    } catch (err) {
      console.error('DeviceSettingsView saveValue error', err);
    }
  };

  const clearValue = async (category, name) => {
    try {
      await setDoc(
        doc(db, 'deviceManagement', familyID, 'userSettings', category),
        { [name]: deleteField() },
        { merge: true },
      );
    } catch (err) {
      console.error('DeviceSettingsView clearValue error', err);
    }
  };

  /* Status line beneath the title — reflects how / when changes take
     effect, using the same "ever signed in / online / offline" signals the
     rest of the app uses. */
  const statusLine = (() => {
    if (family.hasEverSignedIn === false) return 'Your changes apply on first run.';
    if (family.isOnline === false) return 'Your changes apply when it\u2019s back online.';
    return 'Your changes apply right away.';
  })();

  /* Group settings by category while preserving the global-order sort. */
  const grouped = useMemo(() => {
    if (!catalog) return [];
    const out = [];
    const byCat = new Map();
    for (const s of catalog.settings) {
      if (!byCat.has(s.category)) {
        const arr = [];
        byCat.set(s.category, arr);
        out.push({ category: s.category, meta: catalog.categoryMeta[s.category], settings: arr });
      }
      byCat.get(s.category).push(s);
    }
    return out;
  }, [catalog]);

  const overrideCount = useMemo(() => {
    if (!catalog) return 0;
    let n = 0;
    for (const s of catalog.settings) {
      if (userValuesByCategory[s.category]?.[s.name] !== undefined) n += 1;
    }
    return n;
  }, [catalog, userValuesByCategory]);

  return (
    <DetailShell
      onBack={() => navigate(`/app/hearths/${familyID}`)}
      breadcrumb={family.familyName || 'Hearth'}
    >
      <div className="px-4 pt-3 pb-6">
        <div className="mb-4">
          <h1 className="text-[22px] font-bold tracking-[-0.02em] text-slate-900 mb-1">
            Device settings
          </h1>
          <Lede className="text-[13px]">
            Each setting uses its default unless you change it. {statusLine}
          </Lede>
        </div>

        {catalog === null && (
          <HCard className="p-4 mb-4">
            <p className="text-[13px] text-slate-500 text-center">
              Loading settings&hellip;
            </p>
          </HCard>
        )}

        {schemaError && (
          <HCard variant="warn" className="p-4 mb-4">
            <p className="text-[13px] text-slate-700">{schemaError}</p>
          </HCard>
        )}

        {catalog && catalog.settings.length === 0 && !schemaError && (
          <HCard className="p-4 mb-4">
            <p className="text-[13px] text-slate-500 text-center">
              No settings available for this Hearth yet.
            </p>
          </HCard>
        )}

        {grouped.map(({ category, meta, settings }) => {
          const disabledByAutoMode = autoMode && meta?.disabledInAutoMode;
          return (
            <div key={category} className="mb-4">
              {/* Auto-Mode notice sits ABOVE everything else for the
                  category and is NOT dimmed — it explains why the block
                  below is muted. */}
              {disabledByAutoMode && (
                <p className="text-[11px] text-amber-700 font-medium mb-2">
                  Not available while Auto Mode is on
                </p>
              )}
              {/* Everything else in the category (title, description,
                  settings rows + their controls) dims together and blocks
                  input while Auto Mode is on. */}
              <div
                className={cn(
                  disabledByAutoMode && 'opacity-50 pointer-events-none select-none',
                )}
                aria-disabled={disabledByAutoMode || undefined}
              >
                <SectionLabel className="mb-2">
                  {meta?.label || category}
                </SectionLabel>
                {meta?.description && (
                  <p className="text-[12px] text-slate-500 mb-2 leading-relaxed">
                    {meta.description}
                  </p>
                )}
                <HCard className="overflow-hidden">
                  {settings.map((setting, idx) => (
                    <SettingRow
                      key={`${setting.category}/${setting.name}`}
                      setting={setting}
                      userValue={userValuesByCategory[setting.category]?.[setting.name]}
                      defaultValue={effectiveDefaultFor(setting)}
                      disabled={disabledByAutoMode}
                      divider={idx < settings.length - 1}
                      onToggleCustom={(custom) => {
                        if (custom) {
                          saveValue(setting.category, setting.name, effectiveDefaultFor(setting));
                        } else {
                          clearValue(setting.category, setting.name);
                        }
                      }}
                      onChange={(v) => saveValue(setting.category, setting.name, v)}
                    />
                  ))}
                </HCard>
              </div>
            </div>
          );
        })}

        {catalog && catalog.settings.length > 0 && (
          <p className="text-[11px] text-slate-500 text-center mt-2">
            {overrideCount} of {catalog.settings.length} settings manually set
          </p>
        )}
      </div>
    </DetailShell>
  );
}

export default DeviceSettingsView;

